import { useState, useEffect, useRef, useCallback, useMemo, useId } from "react";
import { useNavigate } from "react-router";
import { Search, ArrowRight, FileText, Activity, Shield, CreditCard, Settings, AlertTriangle, BarChart3, Gauge, User, TrendingUp, CheckCircle2, Sliders, Key, Users, Plus, Mail, Lock, Cpu, Webhook, Calendar, Bell, LayoutDashboard, Bot, FileCheck, Loader2, Link2, BookOpen, AlertOctagon, ClipboardCheck, ShieldCheck, Network, Server, FileSignature, ShieldAlert, UserCircle, History, Fingerprint, EyeOff } from "lucide-react";
import apiClient from "../../services/apiClient";
import { useCurrentUser } from "../../hooks/useAPI";

interface CommandPaletteProps {
  isOpen: boolean;
  onClose: () => void;
  isDemoMode?: boolean;
  onViewChange?: (viewId: string) => void;
}

interface Command {
  id: string;
  label: string;
  category: string;
  icon: React.ElementType;
  action: () => void;
  keywords: string[];
  metadata?: string; // Additional info like trace ID, agent status, etc.
}

export function CommandPalette({ isOpen, onClose, isDemoMode = false, onViewChange }: CommandPaletteProps) {
  const navigate = useNavigate();
  const currentUserQuery = useCurrentUser();
  // Narrow once at the boundary instead of `as any` cast every read.
  // useCurrentUser's return type is loose at the api boundary; superAdmin
  // is the only field we read here.
  const userView = currentUserQuery.data as { superAdmin?: boolean } | null | undefined;
  const isFounder = !!userView?.superAdmin;
  const [query, setQuery] = useState("");
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [dynamicCommands, setDynamicCommands] = useState<Command[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const selectedItemRef = useRef<HTMLButtonElement>(null);
  // Focus-restore target — the element that had focus when the palette
  // opened. WAI-ARIA modal-dialog pattern: focus must return to the
  // invoking element on close. Pre-fix focus dropped to <body>, forcing
  // keyboard users to Tab from the top of the page.
  const previousFocusRef = useRef<HTMLElement | null>(null);
  const selectedItemId = useId();
  const listboxId = useId();
  const abortControllerRef = useRef<AbortController | null>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const flattenedCommandsRef = useRef<Command[]>([]);

  const handleNavigate = useCallback((path: string, viewId: string) => {
    if (isDemoMode && onViewChange) {
      // Demo mode: change view internally
      onViewChange(viewId);
    } else {
      // Real mode: navigate to route
      navigate(path);
    }
    onClose();
  }, [isDemoMode, onViewChange, navigate, onClose]);

  const commands: Command[] = useMemo(() => [
    {
      id: "overview",
      label: "Overview Dashboard",
      category: "Navigation",
      icon: LayoutDashboard,
      action: () => handleNavigate("/dashboard", "overview"),
      keywords: ["dashboard", "home", "overview", "metrics", "kpi"]
    },
    {
      id: "cpi",
      label: "CPI Dashboard",
      category: "Navigation",
      icon: TrendingUp,
      action: () => handleNavigate("/dashboard/cpi", "cpi"),
      keywords: ["cpi", "compliance", "performance", "index", "eu ai act"]
    },
    {
      id: "review-queue",
      label: "Review Queue (HITL)",
      category: "Navigation",
      icon: CheckCircle2,
      action: () => handleNavigate("/dashboard/review-queue", "review-queue"),
      keywords: ["review", "queue", "hitl", "human", "approve", "reject"]
    },
    {
      id: "systems",
      label: "AI Systems",
      category: "Navigation",
      icon: Bot,
      action: () => handleNavigate("/dashboard/systems", "systems"),
      keywords: ["systems", "ai systems", "agents", "ai", "models"]
    },
    {
      id: "traces",
      label: "Decision Log",
      category: "Navigation",
      icon: FileText,
      action: () => handleNavigate("/dashboard/traces", "traces"),
      keywords: ["traces", "decisions", "log", "audit", "history"]
    },
    {
      id: "anomalies",
      label: "Anomalies & Drift",
      category: "Navigation",
      icon: Activity,
      action: () => handleNavigate("/dashboard/anomalies", "anomalies"),
      keywords: ["anomalies", "drift", "detection", "baseline", "deviation"]
    },
    {
      id: "feedback-analytics",
      label: "Feedback Analytics",
      category: "Navigation",
      icon: BarChart3,
      action: () => handleNavigate("/dashboard/feedback-analytics", "feedback-analytics"),
      keywords: ["feedback", "corrections", "hitl", "human", "overrides", "analytics"]
    },
    {
      id: "alerts",
      label: "Alerts",
      category: "Navigation",
      icon: AlertTriangle,
      action: () => handleNavigate("/dashboard/alerts", "alerts"),
      keywords: ["alerts", "violations", "compliance", "warnings"]
    },
    {
      id: "policies",
      label: "Policy Engine",
      category: "Navigation",
      icon: Sliders,
      action: () => handleNavigate("/dashboard/policies", "policies"),
      keywords: ["policy", "policies", "rules", "engine", "enforcement"]
    },
    {
      id: "mining",
      label: "Decision Mining",
      category: "Navigation",
      icon: Shield,
      action: () => handleNavigate("/dashboard/mining", "mining"),
      keywords: ["mining", "hitl", "interventions", "enterprise"]
    },
  ], [handleNavigate]);

  // Admin commands - only shown in non-demo mode (order matches Sidebar adminItems)
  const adminCommands: Command[] = useMemo(() => [
    {
      id: "integrations",
      label: "Integration Hub",
      category: "Admin",
      icon: Key,
      action: () => handleNavigate("/dashboard/o/integrations", "o/integrations"),
      keywords: ["integrations", "api", "keys", "webhooks", "sdk"]
    },
    {
      id: "team",
      label: "Team Management",
      category: "Admin",
      icon: Users,
      action: () => handleNavigate("/dashboard/team", "team"),
      keywords: ["team", "users", "members", "roles", "permissions"]
    },
    {
      id: "notifications",
      label: "Notifications",
      category: "Admin",
      icon: Bell,
      action: () => handleNavigate("/dashboard/notifications", "notifications"),
      keywords: ["notifications", "alerts", "messages", "inbox"]
    },
    {
      id: "usage",
      label: "API Usage",
      category: "Admin",
      icon: Gauge,
      action: () => handleNavigate("/dashboard/billing#usage", "usage"),
      keywords: ["usage", "quota", "api", "traces", "limit", "consumption", "billing", "tab"]
    },
    {
      id: "billing",
      label: "Billing & Subscription",
      category: "Admin",
      icon: CreditCard,
      action: () => handleNavigate("/dashboard/billing", "billing"),
      keywords: ["billing", "payment", "subscription", "invoice"]
    },
    {
      id: "scheduled-reports",
      label: "Scheduled Reports",
      category: "Admin",
      icon: Calendar,
      action: () => handleNavigate("/dashboard/reports", "reports"),
      keywords: ["scheduled", "reports", "email", "digest", "recurring", "calendar"]
    },
    {
      id: "activity-log",
      label: "Activity Log",
      category: "Admin",
      icon: FileCheck,
      action: () => handleNavigate("/dashboard/activity-log", "activity-log"),
      // Keep "audit" + "audit log" in keywords so users searching by the old
      // name still find this page after the 2026-05-15 rename.
      keywords: ["activity", "audit", "audit log", "log", "history", "changes", "compliance"]
    },
    {
      id: "settings",
      label: "Settings",
      category: "Admin",
      icon: Settings,
      action: () => handleNavigate("/dashboard/settings", "settings"),
      keywords: ["settings", "config", "preferences"]
    },
  ], [handleNavigate]);

  // Compliance commands - EU AI Act compliance suite (Phase 1+2+3 features)
  const complianceCommands: Command[] = useMemo(() => [
    {
      id: "audit-chain",
      label: "Audit Chain",
      category: "Compliance",
      icon: Link2,
      action: () => handleNavigate("/dashboard/audit/chain", "audit/chain"),
      keywords: ["hash", "chain", "sha256", "merkle", "tamper", "verify", "rfc 8785", "eidas", "integrity", "witness", "anchor"]
    },
    {
      id: "compliance-mapping",
      label: "ISO 42001 Mapping",
      category: "Compliance",
      icon: BookOpen,
      action: () => handleNavigate("/dashboard/compliance/mapping", "compliance/mapping"),
      keywords: ["iso", "42001", "mapping", "clauses", "coverage", "gap"]
    },
    {
      id: "incidents",
      label: "Incident Hub",
      category: "Compliance",
      icon: AlertOctagon,
      action: () => handleNavigate("/dashboard/incidents", "incidents"),
      keywords: ["incident", "hub", "gdpr", "ai act", "dora", "nis2", "cra", "deadline", "breach"]
    },
    // "Auto-Approval Engine" entry removed 2026-06-12: it deep-linked to a
    // #auto-approval anchor on the OLD review queue that the rebuilt page does
    // not have (auto-approval config belongs to the Policy-Engine surface —
    // re-add the command when that page ships).
    {
      id: "deployer",
      label: "Deployer Compliance",
      category: "Compliance",
      icon: ClipboardCheck,
      action: () => handleNavigate("/dashboard/deployer", "deployer"),
      keywords: ["deployer", "art 26", "art 27", "oversight", "dpia", "monitoring"]
    },
    {
      id: "fria",
      label: "FRIA Assessments",
      category: "Compliance",
      icon: ShieldCheck,
      action: () => handleNavigate("/dashboard/deployer?tab=frias", "deployer"),
      keywords: ["fria", "fundamental rights", "impact", "assessment", "art 27", "wizard", "deployer"]
    },
    {
      id: "federated",
      label: "Federated Learning",
      category: "Compliance",
      icon: Network,
      action: () => handleNavigate("/dashboard/federated", "federated"),
      keywords: ["federated", "learning", "privacy", "differential", "rounds", "sigstore"]
    },
    {
      id: "privacy-budget",
      label: "Privacy Budget",
      category: "Compliance",
      icon: Gauge,
      action: () => handleNavigate("/dashboard/federated#privacy-budget", "federated"),
      keywords: ["privacy", "budget", "epsilon", "delta", "differential", "spend", "cap", "tab", "federated"]
    },
    {
      id: "mcp-server",
      label: "MCP Server",
      category: "Admin",
      icon: Server,
      action: () => handleNavigate("/dashboard/o/integrations#mcp", "o/integrations"),
      keywords: ["mcp", "model context protocol", "manifest", "playground", "tools", "resources", "integration"]
    },
    {
      id: "provenance",
      label: "Provenance (C2PA + PROV-O)",
      category: "Compliance",
      icon: FileSignature,
      action: () => handleNavigate("/dashboard/provenance", "provenance"),
      keywords: ["provenance", "c2pa", "prov-o", "signature", "manifest", "claim", "credentials"]
    },
  ], [handleNavigate]);

  // Founder commands - only shown to superAdmin users in non-demo mode
  const founderCommands: Command[] = useMemo(() => [
    {
      id: "super-admin",
      label: "Super Admin Panel",
      category: "Founder",
      icon: ShieldAlert,
      action: () => handleNavigate("/dashboard/super-admin", "super-admin"),
      keywords: ["super", "admin", "panel", "founder", "internal", "system"]
    },
    {
      id: "founder-analytics",
      label: "Founder Analytics",
      category: "Founder",
      icon: BarChart3,
      action: () => handleNavigate("/dashboard/founder-analytics", "founder-analytics"),
      keywords: ["founder", "analytics", "metrics", "internal", "kpi"]
    },
  ], [handleNavigate]);

  // Account sub-pages (under Settings)
  const accountCommands: Command[] = useMemo(() => [
    {
      id: "profile",
      label: "Profile",
      category: "Account",
      icon: UserCircle,
      action: () => handleNavigate("/dashboard/profile", "profile"),
      keywords: ["profile", "name", "avatar", "email", "personal"]
    },
    // These four entries used to point at standalone /security, /passkeys,
    // /login-activity, /privacy routes. After the Profile-tab refactor
    // those routes redirect into Profile tabs, so we link directly to the
    // tab URL to avoid the redirect bounce.
    {
      id: "security-settings",
      label: "Security Settings",
      category: "Account",
      icon: Lock,
      action: () => handleNavigate("/dashboard/profile?tab=security", "profile"),
      keywords: ["security", "password", "2fa", "two factor", "mfa"]
    },
    {
      id: "login-activity",
      label: "Login Activity",
      category: "Account",
      icon: History,
      action: () => handleNavigate("/dashboard/profile?tab=sessions", "profile"),
      keywords: ["login", "activity", "sessions", "history", "ip", "device"]
    },
    {
      id: "passkeys",
      label: "Passkeys",
      category: "Account",
      icon: Fingerprint,
      action: () => handleNavigate("/dashboard/profile?tab=security", "profile"),
      keywords: ["passkeys", "webauthn", "biometric", "fido", "authentication"]
    },
    {
      id: "privacy-consent",
      label: "Privacy & Consent",
      category: "Account",
      icon: EyeOff,
      action: () => handleNavigate("/dashboard/profile?tab=privacy", "profile"),
      keywords: ["privacy", "consent", "gdpr", "cookies", "data", "preferences"]
    },
  ], [handleNavigate]);

  // Live search: fetch real traces, agents, policies, team when query >= 2 chars
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);

    if (query.trim().length < 2) {
      setDynamicCommands([]);
      setIsSearching(false);
      return;
    }

    setIsSearching(true);

    debounceRef.current = setTimeout(async () => {
      if (abortControllerRef.current) abortControllerRef.current.abort();
      const controller = new AbortController();
      abortControllerRef.current = controller;
      const { signal } = controller;
      const q = query.trim();

      try {
        const [
          tracesRes,
          agentsRes,
          policiesRes,
          teamRes,
          incidentsRes,
          friasRes,
          auditRes,
          webhooksRes,
          manifestsRes,
        ] = await Promise.allSettled([
          apiClient.get('/api/traces', { params: { search: q, limit: 5 }, signal }),
          apiClient.get('/api/agents', { params: { search: q, limit: 5 }, signal }),
          apiClient.get('/api/policies', { params: { search: q, limit: 5 }, signal }),
          apiClient.get('/api/team/members', { params: { search: q, limit: 5 }, signal }),
          apiClient.get('/api/incidents', { params: { search: q, limit: 5 }, signal }),
          apiClient.get('/api/fria', { params: { search: q, limit: 5 }, signal }),
          apiClient.get('/api/audit', { params: { search: q, limit: 5 }, signal }),
          apiClient.get('/api/webhooks', { params: { search: q, limit: 5 }, signal }),
          apiClient.get('/api/provenance/c2pa/manifests', { params: { search: q, limit: 5 }, signal }),
        ]);

        if (signal.aborted) return;

        const cmds: Command[] = [];
        const lower = q.toLowerCase();

        // Traces — backend supports ?search= natively
        if (tracesRes.status === 'fulfilled') {
          const envelope = tracesRes.value.data as any;
          const traces: any[] = envelope?.data?.traces || envelope?.traces || [];
          traces.slice(0, 5).forEach((trace: any) => {
            const label = trace.decisionType || (typeof trace.input === 'string' ? trace.input.substring(0, 60) : 'Trace');
            // Canonical confidenceScore resolution: top-level field written by
            // backend confidenceEngine.analyzeConfidence wins over the legacy
            // nested SDK location, which wins over the very-old `confidence` field.
            const conf = trace.confidenceScore ?? trace.outputDecision?.confidenceScore ?? trace.confidence ?? 0;
            cmds.push({
              id: `trace-${trace._id}`,
              label,
              category: 'Recent Traces',
              icon: FileText,
              action: () => handleNavigate(`/dashboard/traces?trace=${trace._id}`, 'traces'),
              keywords: [trace.agentId, trace.status, trace.traceId].filter(Boolean) as string[],
              metadata: `${trace.traceId || String(trace._id).slice(-8)} • ${Math.round(conf * 100)}% • ${trace.status?.toUpperCase() ?? '—'}`,
            });
          });
        }

        // Agents — filter client-side
        if (agentsRes.status === 'fulfilled') {
          const envelope = agentsRes.value.data as any;
          const agents: any[] = envelope?.data || envelope || [];
          (Array.isArray(agents) ? agents : [])
            .filter((a: any) => a.name?.toLowerCase().includes(lower) || a.agentId?.toLowerCase().includes(lower))
            .slice(0, 5)
            .forEach((agent: any) => {
              cmds.push({
                id: `agent-${agent._id}`,
                label: agent.name || agent.agentId || 'Unnamed Agent',
                category: 'Agents',
                icon: Cpu,
                action: () => handleNavigate(`/dashboard/systems?agent=${agent._id}`, 'systems'),
                keywords: [agent.agentId, agent.status].filter(Boolean) as string[],
                metadata: `${agent.agentId ?? '—'} • ${agent.status ?? '—'}`,
              });
            });
        }

        // Policies — filter client-side
        if (policiesRes.status === 'fulfilled') {
          const envelope = policiesRes.value.data as any;
          const policies: any[] = envelope?.data || envelope || [];
          (Array.isArray(policies) ? policies : [])
            .filter((p: any) => p.name?.toLowerCase().includes(lower) || p.description?.toLowerCase().includes(lower))
            .slice(0, 5)
            .forEach((policy: any) => {
              cmds.push({
                id: `policy-${policy._id}`,
                label: policy.name || 'Unnamed Policy',
                category: 'Policies',
                icon: Shield,
                action: () => handleNavigate('/dashboard/policies', 'policies'),
                keywords: [policy.action, policy.description].filter(Boolean) as string[],
                metadata: `${policy.action ?? '—'} • ${policy.isActive ? 'Active' : 'Inactive'}`,
              });
            });
        }

        // Team — filter client-side
        if (teamRes.status === 'fulfilled') {
          const envelope = teamRes.value.data as any;
          const members: any[] = envelope?.data || envelope || [];
          (Array.isArray(members) ? members : [])
            .filter((m: any) =>
              m.name?.toLowerCase().includes(lower) ||
              m.email?.toLowerCase().includes(lower) ||
              m.user?.name?.toLowerCase().includes(lower) ||
              m.user?.email?.toLowerCase().includes(lower)
            )
            .slice(0, 5)
            .forEach((member: any) => {
              const name = member.name || member.user?.name || member.email || 'Team Member';
              const email = member.email || member.user?.email || '';
              cmds.push({
                id: `team-${member._id}`,
                label: name,
                category: 'Team',
                icon: User,
                action: () => handleNavigate('/dashboard/team', 'team'),
                keywords: [email, member.role].filter(Boolean) as string[],
                metadata: `${email || '—'} • ${member.role ?? '—'}`,
              });
            });
        }

        // Incidents — backend supports ?search=
        if (incidentsRes.status === 'fulfilled') {
          const envelope = incidentsRes.value.data as any;
          const incidents: any[] = envelope?.data?.incidents || envelope?.incidents || [];
          incidents.slice(0, 5).forEach((incident: any) => {
            cmds.push({
              id: `incident-${incident._id}`,
              label: incident.title || 'Untitled Incident',
              category: 'Incidents',
              icon: AlertOctagon,
              action: () => handleNavigate(`/dashboard/incidents/${incident._id}`, 'incidents'),
              keywords: [incident.severity, incident.status, ...(incident.regulators || [])].filter(Boolean) as string[],
              metadata: `${(incident.severity ?? '—').toString().toUpperCase()} • ${(incident.status ?? '—').toString().toUpperCase()}${incident.regulators?.length ? ` • ${incident.regulators.join(', ')}` : ''}`,
            });
          });
        }

        // FRIAs — backend supports ?search=
        if (friasRes.status === 'fulfilled') {
          const envelope = friasRes.value.data as any;
          const frias: any[] = envelope?.data?.frias || envelope?.frias || [];
          frias.slice(0, 5).forEach((fria: any) => {
            cmds.push({
              id: `fria-${fria._id}`,
              label: fria.systemName || 'Untitled FRIA',
              category: 'FRIAs',
              icon: ShieldCheck,
              action: () => handleNavigate('/dashboard/deployer?tab=frias', 'deployer'),
              keywords: [fria.status, fria.purpose].filter(Boolean) as string[],
              metadata: `${(fria.status ?? '—').toString().toUpperCase()}${typeof fria.completeness === 'number' ? ` • ${fria.completeness}% complete` : ''}`,
            });
          });
        }

        // Audit Log — backend supports ?search=
        if (auditRes.status === 'fulfilled') {
          const envelope = auditRes.value.data as any;
          const entries: any[] = envelope?.data?.entries || envelope?.entries || [];
          entries.slice(0, 5).forEach((entry: any) => {
            const label = entry.action || 'Audit Entry';
            cmds.push({
              id: `audit-${entry._id}`,
              label,
              category: 'Activity Log',
              icon: FileCheck,
              action: () => handleNavigate('/dashboard/activity-log', 'activity-log'),
              keywords: [entry.userEmail, entry.resource, entry.resourceId, entry.status].filter(Boolean) as string[],
              metadata: `${entry.userEmail || '—'}${entry.resource ? ` • ${entry.resource}` : ''}${entry.status ? ` • ${entry.status}` : ''}`,
            });
          });
        }

        // Webhooks — backend supports ?search=
        if (webhooksRes.status === 'fulfilled') {
          const envelope = webhooksRes.value.data as any;
          const webhooks: any[] = envelope?.data || envelope?.webhooks || [];
          (Array.isArray(webhooks) ? webhooks : []).slice(0, 5).forEach((wh: any) => {
            const url = wh.url || '—';
            const shortUrl = url.length > 50 ? `${url.slice(0, 50)}…` : url;
            cmds.push({
              id: `webhook-${wh._id}`,
              label: wh.description || shortUrl,
              category: 'Webhooks',
              icon: Webhook,
              action: () => handleNavigate('/dashboard/o/integrations?tab=webhooks', 'o/integrations'),
              keywords: [wh.url, wh.description, ...(wh.events || [])].filter(Boolean) as string[],
              metadata: `${shortUrl}${wh.isActive === false ? ' • INACTIVE' : ' • ACTIVE'}`,
            });
          });
        }

        // C2PA Manifests — backend supports ?search=
        if (manifestsRes.status === 'fulfilled') {
          const envelope = manifestsRes.value.data as any;
          const manifests: any[] = envelope?.data?.manifests || envelope?.manifests || [];
          manifests.slice(0, 5).forEach((m: any) => {
            cmds.push({
              id: `manifest-${m._id}`,
              label: m.label || `Manifest ${String(m._id).slice(-8)}`,
              category: 'C2PA Manifests',
              icon: FileSignature,
              action: () => handleNavigate('/dashboard/provenance', 'provenance'),
              keywords: [m.traceId, m.contentHash, m.label].filter(Boolean) as string[],
              metadata: `${m.traceId ? `trace ${String(m.traceId).slice(-8)}` : '—'}${m.revokedAt ? ' • REVOKED' : ' • ACTIVE'}`,
            });
          });
        }

        setDynamicCommands(cmds);
      } catch (err: unknown) {
        // Narrow safely instead of `err: any` blanket. AbortController
        // cancellations come through as { name: 'CanceledError', code:
        // 'ERR_CANCELED' } from axios — both forms guarded.
        const e = err as { name?: string; code?: string } | null;
        if (e?.name === 'CanceledError' || e?.code === 'ERR_CANCELED') return;
        setDynamicCommands([]);
      } finally {
        if (!signal.aborted) setIsSearching(false);
      }
    }, 300);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [query]);

  // Cleanup abort controller on unmount
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) abortControllerRef.current.abort();
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  // Quick Actions - Fast shortcuts for common tasks
  const quickActionCommands: Command[] = useMemo(() => [
    {
      id: "action-create-api-key",
      label: "Create new API Key",
      category: "Quick Actions",
      icon: Plus,
      action: () => {
        handleNavigate("/dashboard/o/integrations", "o/integrations");
        // In real app: also trigger modal
      },
      keywords: ["create", "new", "api", "key", "generate", "token"]
    },
    {
      id: "action-invite-member",
      label: "Invite Team Member",
      category: "Quick Actions",
      icon: Mail,
      action: () => {
        handleNavigate("/dashboard/team", "team");
        // In real app: also trigger modal
      },
      keywords: ["invite", "team", "member", "add", "user", "colleague"]
    },
    {
      id: "action-enable-2fa",
      label: "Enable Two-Factor Authentication",
      category: "Quick Actions",
      icon: Lock,
      action: () => handleNavigate("/dashboard/settings?section=security", "settings"),
      keywords: ["enable", "2fa", "two", "factor", "authentication", "security"]
    },
    {
      id: "action-view-activity-log",
      label: "View Activity Log",
      category: "Quick Actions",
      icon: FileCheck,
      action: () => handleNavigate("/dashboard/activity-log", "activity-log"),
      keywords: ["view", "activity", "audit", "audit log", "log", "history", "changes"]
    },
    {
      id: "action-create-webhook",
      label: "Create Webhook",
      category: "Quick Actions",
      icon: Webhook,
      action: () => handleNavigate("/dashboard/o/integrations?tab=webhooks", "o/integrations"),
      keywords: ["create", "webhook", "integration", "endpoint", "notification"]
    },
  ], [handleNavigate]);

  // Combine commands: static nav/actions + live search results
  const allCommands = isDemoMode
    ? [...commands, ...complianceCommands, ...accountCommands, ...quickActionCommands, ...dynamicCommands]
    : [
        ...commands,
        ...adminCommands,
        ...complianceCommands,
        ...(isFounder ? founderCommands : []),
        ...accountCommands,
        ...quickActionCommands,
        ...dynamicCommands,
      ];

  // Fuzzy search scoring function
  const fuzzyScore = (text: string, query: string): number => {
    text = text.toLowerCase();
    query = query.toLowerCase();
    
    // Exact match = highest score
    if (text === query) return 1000;
    
    // Starts with = very high score
    if (text.startsWith(query)) return 800;
    
    // Word boundary match = high score
    const words = text.split(/\s+/);
    if (words.some(word => word.startsWith(query))) return 600;
    
    // Contains = medium score
    if (text.includes(query)) return 400;
    
    // Fuzzy match (all chars in order)
    let queryIndex = 0;
    let score = 200;
    for (let i = 0; i < text.length && queryIndex < query.length; i++) {
      if (text[i] === query[queryIndex]) {
        queryIndex++;
        score += 10;
      }
    }
    
    return queryIndex === query.length ? score : 0;
  };

  // Search with relevance scoring — memoized so 100+ commands × dynamic
  // search additions don't re-fuzzy-score on every keystroke or arrow key.
  // Dependencies are exactly the inputs to the computation; React-Query
  // returns referentially-stable arrays so the memo only invalidates
  // when the user actually types or new dynamic results arrive.
  const filteredCommands = useMemo(() => {
    if (query.trim() === "") return allCommands;
    return allCommands
      .map(cmd => {
        const labelScore = fuzzyScore(cmd.label, query);
        const keywordScore = Math.max(
          ...cmd.keywords.map(kw => fuzzyScore(kw, query)),
          0,
        );
        const metadataScore = cmd.metadata ? fuzzyScore(cmd.metadata, query) : 0;
        const totalScore = Math.max(labelScore, keywordScore, metadataScore);
        return { cmd, score: totalScore };
      })
      .filter(({ score }) => score > 0)
      .sort((a, b) => b.score - a.score)
      .map(({ cmd }) => cmd);
  }, [allCommands, query]);

  // Group commands by category
  const groupedCommands = useMemo(
    () =>
      filteredCommands.reduce((acc, cmd) => {
        if (!acc[cmd.category]) acc[cmd.category] = [];
        acc[cmd.category].push(cmd);
        return acc;
      }, {} as Record<string, Command[]>),
    [filteredCommands],
  );

  // Define category order
  const categoryOrder = useMemo(
    () =>
      query.trim() === ""
        ? ["Navigation", "Admin", "Compliance", "Founder", "Account", "Quick Actions"]
        : Object.keys(groupedCommands),
    [query, groupedCommands],
  );

  // Flatten for keyboard navigation
  const flattenedCommands = useMemo(
    () => categoryOrder.flatMap(category => groupedCommands[category] || []),
    [categoryOrder, groupedCommands],
  );
  flattenedCommandsRef.current = flattenedCommands;

  // Text highlighting helper
  const highlightMatch = (text: string, query: string) => {
    if (!query.trim()) return text;

    const escaped = query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const parts = text.split(new RegExp(`(${escaped})`, 'gi'));
    return parts.map((part, i) =>
      part.toLowerCase() === query.toLowerCase()
        // Brand-dilution sweep: brand color is reserved for primary
        // CTA + sidebar active stripe + unread notification dot. A
        // search highlight is informational, not "Adjudon attention" —
        // foreground/10 + foreground text matches the muted-accent
        // pattern other highlights use (DecisionLog row hover etc).
        ? <mark key={i} className="bg-foreground/10 text-foreground">{part}</mark>
        : part
    );
  };

  useEffect(() => {
    if (isOpen) {
      // Capture invoker BEFORE moving focus into the palette so we can
      // hand it back on close (WAI-ARIA dialog pattern).
      previousFocusRef.current = (document.activeElement as HTMLElement) ?? null;
      inputRef.current?.focus();
      return;
    }
    // On close (isOpen flipped false), restore focus to the invoker.
    // Guard against the invoker being unmounted (e.g., user clicked
    // a Sidebar item that re-rendered the page) by checking the
    // element is still in the document.
    const prev = previousFocusRef.current;
    if (prev && document.contains(prev)) {
      prev.focus();
    }
  }, [isOpen]);

  useEffect(() => {
    setSelectedIndex(0);
  }, [query]);

  // Auto-scroll selected item into view. Respect prefers-reduced-motion
  // — pre-fix smooth scroll fired even for users who disabled animation.
  useEffect(() => {
    if (!selectedItemRef.current) return;
    const reduceMotion = typeof window !== 'undefined'
      && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    selectedItemRef.current.scrollIntoView({
      behavior: reduceMotion ? 'auto' : 'smooth',
      block: 'nearest',
    });
  }, [selectedIndex]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!isOpen) return;
      const cmds = flattenedCommandsRef.current;

      if (e.key === "ArrowDown") {
        e.preventDefault();
        if (cmds.length === 0) return;
        setSelectedIndex(prev => (prev + 1) % cmds.length);
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        if (cmds.length === 0) return;
        setSelectedIndex(prev => (prev - 1 + cmds.length) % cmds.length);
      } else if (e.key === "Enter") {
        e.preventDefault();
        const cmd = cmds[selectedIndex];
        if (cmd) runCommand(cmd);
      } else if (e.key === "Escape") {
        e.preventDefault();
        handleClose();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, selectedIndex]);

  const handleClose = () => {
    setQuery("");
    setSelectedIndex(0);
    onClose();
  };

  // Single entry point for "user picked this command" — fired from both
  // the click handler and the Enter key handler. Pre-fix the same two-line
  // pattern lived at both call sites (`cmd.action(); handleClose();`) so
  // any cleanup added on activation had to be added in two places.
  const runCommand = (cmd: Command) => {
    cmd.action();
    handleClose();
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black/60 z-50 animate-in fade-in-0 duration-200"
        onClick={handleClose}
      />

      {/* Command Palette */}
      <div className="fixed inset-0 z-50 flex items-start justify-center pt-[20vh] pointer-events-none">
        <div 
          className="w-[90vw] max-w-[760px] pointer-events-auto animate-in fade-in-0 slide-in-from-top-4 duration-300"
          onClick={(e) => e.stopPropagation()}
        >
          {/* role=dialog without aria-modal — aria-modal=true is a
              contract that says "focus is trapped within this dialog"
              and the palette intentionally does NOT trap focus (matches
              Stripe / Linear / Raycast patterns: Tab leaves the palette,
              ESC dismisses). aria-modal without a real trap confuses SR
              announcement ("out of dialog" when user Tabs out). */}
          <div className="adp-modal overflow-hidden" role="dialog" aria-label="Command palette">
            {/* Search Input */}
            <div className="flex items-center gap-3 px-4 py-3 border-b border-border">
              <Search aria-hidden="true" className="w-4 h-4 text-muted-foreground flex-shrink-0" />
              <input
                ref={inputRef}
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search pages, traces, agents, policies, team, incidents, FRIAs, audit log, webhooks, manifests…"
                // WAI-ARIA combobox+listbox pattern: input owns the list,
                // and aria-activedescendant points at the focused option
                // without moving DOM focus (so typing keeps working).
                role="combobox"
                aria-expanded={filteredCommands.length > 0}
                aria-controls={listboxId}
                aria-autocomplete="list"
                aria-activedescendant={
                  filteredCommands.length > 0 && flattenedCommands[selectedIndex]
                    ? `${selectedItemId}-${flattenedCommands[selectedIndex].id}`
                    : undefined
                }
                aria-label="Search commands"
                className="flex-1 bg-transparent text-sm text-foreground placeholder:text-muted-foreground focus:outline-none rounded-none"
              />

            </div>

            {/* Results */}
            <div className="max-h-[400px] overflow-y-auto">
              {isSearching && query.trim().length >= 2 ? (
                <div role="status" aria-live="polite" className="px-4 py-8 flex items-center justify-center gap-2 text-muted-foreground">
                  <Loader2 aria-hidden="true" className="w-4 h-4 animate-spin motion-reduce:animate-none" />
                  <span className="text-sm">Searching…</span>
                </div>
              ) : filteredCommands.length === 0 ? (
                // role=status so SR users hear the new state when results
                // collapse to empty (e.g., they typed past the matching
                // characters). Without it, the table just disappeared.
                <div role="status" aria-live="polite" className="px-4 py-8 text-center">
                  <p className="text-sm text-muted-foreground">{query.trim().length >= 2 ? 'No results found' : 'Type to search traces, agents, policies, team…'}</p>
                </div>
              ) : (
                // role=listbox so SR users get "list with N options"
                // announcement and arrow-key navigation works through
                // the rotor. Each item gets role=option + aria-selected
                // + a stable id so the input's aria-activedescendant
                // can point at it without moving DOM focus.
                <div id={listboxId} role="listbox" aria-label="Search results" className="py-2">
                  {categoryOrder.map(category => {
                    const categoryCommands = groupedCommands[category];
                    if (!categoryCommands) return null;

                    return (
                      <div key={category} role="group" aria-label={category}>
                        <div className="px-4 py-2 text-[10px] text-mono text-muted-foreground uppercase tracking-wider">
                          {category}
                        </div>
                        {categoryCommands.map((cmd) => {
                          const Icon = cmd.icon;
                          const globalIndex = flattenedCommands.findIndex(c => c.id === cmd.id);
                          const isSelected = globalIndex === selectedIndex;

                          return (
                            <button
                              key={cmd.id}
                              id={`${selectedItemId}-${cmd.id}`}
                              role="option"
                              aria-selected={isSelected}
                              type="button"
                              // tabIndex=-1 so Tab does NOT move DOM focus
                              // to options. The combobox+listbox pattern
                              // keeps focus on the input; aria-activedescendant
                              // signals which option is "selected" without
                              // moving focus. Pre-fix Tab moved focus to
                              // options, conflicting with the
                              // aria-activedescendant signal SR users heard.
                              tabIndex={-1}
                              onClick={() => runCommand(cmd)}
                              // Selected-row mirrors Sidebar Active vocabulary
                              // — Maritime Blue left-rule + brand icon + brand
                              // title text on a lifted surface-1 card. Same
                              // "this is what activates on Enter" semantic as
                              // "this is the page you're on" in the sidebar,
                              // so they share the same brand language.
                              className={`w-full flex items-center gap-3 px-4 py-2.5 text-left transition-all duration-150 motion-reduce:transition-none ${
                                isSelected
                                  ? "bg-surface-1 border-l-2 border-[var(--brand)]"
                                  : "border-l-2 border-transparent hover:bg-surface-1/50"
                              }`}
                              ref={isSelected ? selectedItemRef : null}
                            >
                              <Icon aria-hidden="true" className={`w-4 h-4 flex-shrink-0 transition-colors duration-150 motion-reduce:transition-none ${
                                isSelected ? "text-[var(--brand)]" : "text-muted-foreground"
                              }`} />
                              <div className="flex-1 min-w-0">
                                <div className={`text-sm transition-colors duration-150 motion-reduce:transition-none ${
                                  isSelected ? "text-[var(--brand)] font-medium" : "text-foreground"
                                }`}>
                                  {highlightMatch(cmd.label, query)}
                                </div>
                                {cmd.metadata && (
                                  <div className="text-[10px] text-mono text-muted-foreground mt-0.5">
                                    {cmd.metadata}
                                  </div>
                                )}
                              </div>
                              {isSelected && (
                                <ArrowRight aria-hidden="true" className="w-3.5 h-3.5 text-[var(--brand)] animate-in fade-in-0 slide-in-from-left-1 duration-200 motion-reduce:animate-none" />
                              )}
                            </button>
                          );
                        })}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Footer Hint */}
            <div className="px-4 py-2 border-t border-border bg-surface-1 flex items-center justify-between">
              <div className="flex items-center gap-3 text-[10px] text-muted-foreground">
                <span className="flex items-center gap-1">
                  <kbd className="px-1.5 py-0.5 bg-surface-2 border border-border text-mono">↑</kbd>
                  <kbd className="px-1.5 py-0.5 bg-surface-2 border border-border text-mono">↓</kbd>
                  Navigate
                </span>
                <span className="flex items-center gap-1">
                  <kbd className="px-1.5 py-0.5 bg-surface-2 border border-border text-mono">↵</kbd>
                  Select
                </span>
              </div>
              <span className="flex items-center gap-1 text-[10px] text-muted-foreground">
                <kbd className="px-1.5 py-0.5 bg-surface-2 border border-border text-mono">Esc</kbd>
                Close
              </span>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}